import { formatCurrency } from '../utils/dateutils';
import { firstScalar } from '../claim-intake/claimIntakeParser';

export const CLAIM_INTAKE_HIDDEN_KEYS = new Set([
  'source_id',
  'file_name',
  'aiworker_usage_id',
  'session_id',
]);

export const CLAIM_INTAKE_LABOR_FIELD_PRIORITY = [
  'amount',
  'labor_amount',
  'labor_description',
  'labor_hours',
  'labor_op_code',
  'labor_opcode',
  'labor_id',
  'component',
  'loss_code',
  'loss_code_description',
];

export const CLAIM_INTAKE_PART_FIELD_PRIORITY = [
  'part_amount',
  'amount',
  'part_description',
  'part_number',
  'part_quantity',
  'part_unit_price',
  'unit_cost',
  'part_id',
];

export const CLAIM_INTAKE_SECTION_CLASS = 'border-default-200 bg-content2/80';

export function formatClaimIntakeDisplayValue(value: unknown): string {
  if (value == null || value === '') return '--';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '--';
  if (typeof value === 'string') return value.trim() || '--';
  if (Array.isArray(value)) return value.length ? `${value.length} item(s)` : '--';
  return '--';
}

function formatAddress(value: unknown): string {
  if (typeof value === 'string') return value.trim() || '--';
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '--';
  const addr = value as Record<string, unknown>;
  const parts = [
    addr.address1,
    addr.address2,
    addr.city,
    addr.state,
    addr.postal_code ?? addr.postalCode,
    addr.country_code ?? addr.countryCode ?? addr.country,
  ]
    .map((part) => {
      if (part == null) return '';
      if (typeof part === 'string' || typeof part === 'number' || typeof part === 'boolean') {
        return String(part).trim();
      }
      return '';
    })
    .filter(Boolean);
  return parts.length ? parts.join(', ') : '--';
}

function isCurrencyKey(key: string): boolean {
  const lower = key.toLowerCase();
  return (
    (lower.includes('amount') ||
      lower.includes('price') ||
      lower.includes('cost') ||
      lower.includes('rate') ||
      lower.includes('deductible')) &&
    !lower.includes('percentage')
  );
}

function formatSimpleObject(value: Record<string, unknown>): string {
  if (typeof value.description === 'string' && value.description.trim()) {
    return typeof value.code === 'string' && value.code.trim()
      ? `${value.code} — ${value.description}`
      : value.description;
  }
  if (typeof value.code === 'string' && value.code.trim()) return value.code;
  if (typeof value.name === 'string' && value.name.trim()) return value.name;
  return Object.keys(value).length ? JSON.stringify(value) : '--';
}

export function formatClaimIntakeFieldValue(key: string, value: unknown): string {
  if (value == null || value === '') return '--';
  if (Array.isArray(value)) {
    const first = value.find((item) => item != null && item !== '');
    if (first == null) return '--';
    if (typeof first === 'object') return `${value.length} item(s)`;
    return firstScalar(value);
  }
  if (typeof value === 'object') {
    const lower = key.toLowerCase();
    if (lower.includes('address')) return formatAddress(value);
    return formatSimpleObject(value as Record<string, unknown>);
  }
  if (isCurrencyKey(key) && (typeof value === 'number' || (typeof value === 'string' && !Number.isNaN(Number(value))))) {
    return formatCurrency(Number(value));
  }
  return formatClaimIntakeDisplayValue(value);
}

export function asClaimIntakeRecord(value: unknown): Record<string, unknown> | null {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

/** First workflow payload row (`data[0]` or nested `data` object). */
export function getClaimIntakePayloadRow(data: unknown): Record<string, unknown> | null {
  const root = asClaimIntakeRecord(data);
  if (!root) return null;
  if (Array.isArray(root.data) && root.data.length > 0) {
    return asClaimIntakeRecord(root.data[0]) ?? root;
  }
  const nestedData = asClaimIntakeRecord(root.data);
  if (nestedData) return nestedData;
  return root;
}

/** `parser_results` from claim-intake `action_output.estimateparser` (agent history side panel). */
export function extractEstimateParserResultsFromUsageData(
  data: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  const actionOutput = asClaimIntakeRecord(data?.action_output);
  if (!actionOutput) return null;

  const estimateParserKey = Object.keys(actionOutput).find((key) => {
    const normalized = key.toLowerCase().replace(/[\s_-]/g, '');
    return normalized === 'estimateparser' || normalized.includes('estimateparser');
  });
  if (!estimateParserKey) return null;

  const row = getClaimIntakePayloadRow(actionOutput[estimateParserKey]);
  if (!row) return null;

  const parserResults = asClaimIntakeRecord(row.parser_results);
  return parserResults;
}

/** Merges `contracts` from VIN lookup responses (root + each `data[]` row). */
export function extractContractsFromActionOutput(data: unknown): Record<string, unknown>[] {
  const root = asClaimIntakeRecord(data);
  if (!root) return [];

  const merged: Record<string, unknown>[] = [];
  const rootContracts = root.contracts;
  if (Array.isArray(rootContracts)) {
    for (const item of rootContracts) {
      const rec = asClaimIntakeRecord(item);
      if (rec) merged.push(rec);
    }
  }

  const dataRows = root.data;
  if (Array.isArray(dataRows)) {
    for (const row of dataRows) {
      const rec = asClaimIntakeRecord(row);
      if (!rec) continue;
      const contracts = rec.contracts;
      if (!Array.isArray(contracts)) continue;
      for (const item of contracts) {
        const contract = asClaimIntakeRecord(item);
        if (contract) merged.push(contract);
      }
    }
  }

  return merged;
}

export function hasClaimIntakeRenderablePayload(data: unknown): boolean {
  if (extractContractsFromActionOutput(data).length > 0) return true;

  const root = asClaimIntakeRecord(data);
  if (!root) return false;

  const row = getClaimIntakePayloadRow(data);
  if (!row) return false;

  if (row.parser_results || row.mapped_output || row['3Cs']) return true;
  if (row.customer || row.servicer || row.requests || row.product || row.policies) return true;
  if (row.vin || row.Vehicle_Identification_Number || row.vehicle) return true;
  if (row.contract_number || row.contractNumber) return true;

  return Object.keys(row).some(
    (key) => !CLAIM_INTAKE_HIDDEN_KEYS.has(key) && key !== 'body' && key !== 'error'
  );
}

export function orderClaimIntakeRowKeys(row: Record<string, unknown>, priority: string[]): string[] {
  const keys = Object.keys(row).filter((key) => key !== 'rowId' && !CLAIM_INTAKE_HIDDEN_KEYS.has(key));
  const sorted: string[] = [];
  const remaining = new Set(keys);

  for (const priorityKey of priority) {
    const match = keys.find(
      (key) =>
        key.toLowerCase() === priorityKey.toLowerCase() ||
        key.toLowerCase().includes(priorityKey.toLowerCase())
    );
    if (match && remaining.has(match)) {
      sorted.push(match);
      remaining.delete(match);
    }
  }

  return [...sorted, ...[...remaining].sort((a, b) => a.localeCompare(b))];
}
